import { useState } from "react";
import { useNavigate } from "react-router-dom";
// eslint-disable-next-line import/no-unresolved
import { PanelCardFooter, PanelShowMoreLink } from "@astrana/ui/modules";
import { MemberSearchCard } from "../members/MemberSearchCard";
import type { SearchFilters } from "../members/MemberResultsTable";
import { useMemberSearch } from "../members/useMemberSearch";
import { validateMemberSearch } from "../members/memberSearchValidation";

/**
 * Member search, hosted as a Home widget.
 *
 * WHY this exists alongside `MemberSearchPage`: Home's other two widgets
 * (UrgentPatientsWidget / HospitalEdEventsWidget) both render nothing without
 * a Provider Portal panel, so before a clinician links their PP account Home
 * was a heading over empty space. Search is the one thing that works for
 * every user on day one, so it becomes Home's floor — the page always has
 * something to do.
 *
 * This is NOT a re-mount of `MemberSearchPage`, deliberately. That page
 * carries the Elation/EHR bridge — `useElationBridge`, the sticky chart
 * scrape, `useResolvedMember`, and the auto-search → auto-navigate chain
 * guarded by the module-level `autoNav` singleton. A second mount of that
 * logic would race the same guard and could yank a user off Home into a
 * member profile. So this widget owns only the state that is safe to
 * duplicate: the fields, the 2-char/MPI validation rule and `useMemberSearch`.
 * Everything EHR-shaped stays on the page.
 *
 * The card itself — form, results, styling — is `MemberSearchCard`, the SAME
 * component the page renders, so the two cannot drift apart. This widget only
 * adds Home's scroll cap on the results and the "Open in full search" footer.
 */

/**
 * Scroll cap for the results list, sized to show FOUR full rows.
 *
 * A `MemberResultsTable` row now measures ~68px (`py-2.5` = 20, identity line
 * ~24, `gap-1` = 4, one meta line ~20), separated by a 1px divider rather
 * than a 12px gap — the rows lost their nested-card chrome. Four rows is
 * therefore ~276, plus the table's own "N results" count line (~20) and its
 * gap (12) = ~308 (~19rem).
 *
 * Set to 22rem (352px), not trimmed to 19: four rows is the floor asked for,
 * not the target, so this shows a fifth and absorbs a row whose IPA/plan
 * pills wrap onto a second line. It is a MAX, so one or two results still
 * render at their natural height and the card stays short.
 *
 * Re-measure if the row markup in MemberResultsTable.tsx changes.
 */
const RESULTS_MAX_H = "max-h-[22rem]";

const EMPTY_FILTERS: SearchFilters = {
  firstName: "",
  lastName: "",
  mpi: "",
  dob: "",
};

export default function MemberSearchWidget() {
  const navigate = useNavigate();
  const { search, results, loading, error, requiresDob } = useMemberSearch();
  const [filters, setFilters] = useState<SearchFilters>(EMPTY_FILTERS);
  const [submitted, setSubmitted] = useState<SearchFilters | null>(null);
  const [showIneligible, setShowIneligible] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateField = (key: keyof SearchFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setValidationError(null);
  };

  const runSearch = (next: SearchFilters, ineligible: boolean) =>
    void search({
      firstName: next.firstName.trim() || undefined,
      lastName: next.lastName.trim() || undefined,
      mpi: next.mpi.trim() || undefined,
      dob: next.dob.trim() || undefined,
      showIneligibleMembers: ineligible,
    });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Same gate as MemberSearchPage's `onSubmit` — see memberSearchValidation.
    const invalid = validateMemberSearch(filters, { requiresDob });
    if (invalid) {
      setValidationError(invalid);
      return;
    }
    setValidationError(null);
    setSubmitted(filters);
    runSearch(filters, showIneligible);
  };

  const onClear = () => {
    setFilters(EMPTY_FILTERS);
    setSubmitted(null);
    setValidationError(null);
  };

  /**
   * Hand the current search to the full page, which restores it from route
   * state (`MemberSearchPage` reads `location.state.searchFilters`). Now that
   * the header no longer carries a Search tab, this link and a member profile
   * are the only ways a user reaches that page, so it renders whenever there
   * are results rather than only on overflow.
   */
  const openFullSearch = () => {
    navigate("/members", {
      state: submitted ? { searchFilters: submitted } : undefined,
    });
  };

  return (
    <MemberSearchCard
      idPrefix="home-ms"
      filters={filters}
      onFieldChange={updateField}
      showIneligible={showIneligible}
      onShowIneligibleChange={(next) => {
        setShowIneligible(next);
        // Re-run the CURRENT search (page parity) so the rows update without
        // a second click; with nothing submitted yet it just arms the next one.
        if (submitted) runSearch(submitted, next);
      }}
      onSubmit={onSubmit}
      onClear={onClear}
      loading={loading}
      validationError={validationError}
      error={error}
      submitted={submitted}
      results={results}
      requiresDob={requiresDob}
      resultsMaxHeightClass={RESULTS_MAX_H}
      footer={
        <PanelCardFooter>
          <PanelShowMoreLink onClick={openFullSearch} label="Open in full search" />
        </PanelCardFooter>
      }
    />
  );
}
